/** Кэш колонки «Ударения»: точечные правки по ответам и событиям (спек «Реальное время»). */
import type { InfiniteData, QueryClient } from '@tanstack/react-query';
import type { BookTextProgress, GlossaryData, StressEntry, Suggestion, SuggestionSection, SuggestionsPage } from '@/api/glossaryModel';

export const glossaryQueryKey = (projectId: string) => ['glossary', projectId] as const;
/** Без section — префикс обеих частей: инвалидация по нему задевает и частые, и «один раз». */
export const suggestionsQueryKey = (projectId: string, section?: SuggestionSection) =>
  section ? (['glossarySuggestions', projectId, section] as const) : (['glossarySuggestions', projectId] as const);

const collator = new Intl.Collator('ru');
export const sortEntries = (list: StressEntry[]) => [...list].sort((a, b) => collator.compare(a.word, b.word));

export function upsertEntry(data: GlossaryData | undefined, entry: StressEntry): GlossaryData | undefined {
  if (!data) return data;
  const prev = [...data.entries, ...data.shared].find((x) => x.entry_id === entry.entry_id);
  if (prev && prev.updated_at > entry.updated_at) return data;
  const without = (list: StressEntry[]) => list.filter((x) => x.entry_id !== entry.entry_id);
  const entries = without(data.entries);
  const shared = without(data.shared);
  // Число «в книге» у общей записи — своё для каждого проекта: событие без числа его не затирает
  const next = prev && entry.count === 0 && prev.count > 0 ? { ...entry, count: prev.count } : entry;
  return entry.scope === 'book'
    ? { ...data, entries: sortEntries([...entries, next]), shared }
    : { ...data, entries, shared: sortEntries([...shared, next]) };
}

export function removeEntry(data: GlossaryData | undefined, entryId: string): GlossaryData | undefined {
  if (!data) return data;
  return { ...data, entries: data.entries.filter((x) => x.entry_id !== entryId), shared: data.shared.filter((x) => x.entry_id !== entryId) };
}

/** Событие project:glossaryChanged (сервер: realtime/events.ts, GlossaryChangedPayload). */
export type GlossaryChangedPayload = { projectId: string; entry: StressEntry } | { projectId: string; entry_id: string; deleted: true };

/** Открытый глоссарий правится точечно; закрытый не создаётся — загрузится при открытии. Предложения пересоберёт сервер. */
export function applyGlossaryChanged(qc: QueryClient, payload: GlossaryChangedPayload): void {
  const key = glossaryQueryKey(payload.projectId);
  if (qc.getQueryData(key) === undefined) return;
  qc.setQueryData<GlossaryData>(key, (prev) => ('deleted' in payload ? removeEntry(prev, payload.entry_id) : upsertEntry(prev, payload.entry)));
  void qc.invalidateQueries({ queryKey: suggestionsQueryKey(payload.projectId) });
}

export const suggestionKey = (s: Suggestion) => (s.kind === 'word' ? s.word_key : s.form_key);

export function removeSuggestion(data: InfiniteData<SuggestionsPage> | undefined, key: string): InfiniteData<SuggestionsPage> | undefined {
  if (!data) return data;
  let removed = 0;
  const pages = data.pages.map((p) => {
    const items = p.items.filter((s) => suggestionKey(s) !== key);
    removed += p.items.length - items.length;
    return { ...p, items };
  });
  return { ...data, pages: pages.map((p) => ({ ...p, total: Math.max(0, p.total - removed) })) };
}

/** Файл проекта (project:updated или список карточки) — только то, от чего зависит колонка */
export type ProjectFileRef = { file_id: string; file_name: string; file_type: string | null; file_pages?: number | null };

const sameSet = (a: readonly string[], b: readonly string[]) => a.length === b.length && [...a].sort().join('\n') === [...b].sort().join('\n');

/**
 * Глоссарии, показанные колонкой по своему списку раньше сервера, и файлы в них, которых сервер ещё не подтвердил:
 * ждут сверки (страницы, «разбор устарел»), а «Разобрать» по такому файлу ждёт её же (serverHasFiles)
 */
const AHEAD = new WeakMap<QueryClient, Map<string, ReadonlySet<string>>>();
/** Подписки на ответы сервера — одна на QueryClient */
const WATCHED = new WeakSet<QueryClient>();

/**
 * Ответ сервера на глоссарий (не своя правка кэша: у setQueryData — manual) — показанные раньше файлы им сверены.
 * Своя правка (идёт разбор, ход разбора) пометку не снимает.
 */
function aheadOf(qc: QueryClient): Map<string, ReadonlySet<string>> {
  let map = AHEAD.get(qc);
  if (!map) {
    const fresh = new Map<string, ReadonlySet<string>>();
    AHEAD.set(qc, fresh);
    map = fresh;
  }
  if (!WATCHED.has(qc)) {
    WATCHED.add(qc);
    qc.getQueryCache().subscribe((event) => {
      if (event.type !== 'updated' || event.action.type !== 'success' || event.action.manual) return;
      const [head, projectId] = event.query.queryKey as readonly unknown[];
      if (head === glossaryQueryKey('')[0] && typeof projectId === 'string') AHEAD.get(qc)?.delete(projectId);
    });
  }
  return map;
}
const hasAhead = (qc: QueryClient, projectId: string) => (AHEAD.get(qc)?.get(projectId)?.size ?? 0) > 0;
/** Сколько «Разобрать» ждёт подтверждения типа файла сервером */
const CONFIRM_WAIT_MS = 15_000;

const fileIdsOf = (data: GlossaryData) => [...data.text_files.map((f) => f.file_id), ...data.glossary_file_ids];

function sameFiles(data: GlossaryData, files: readonly ProjectFileRef[]): boolean {
  const texts = files.filter((f) => f.file_type === 'Текст').map((f) => `${f.file_id}:${f.file_name}`);
  const glossaries = files.filter((f) => f.file_type === 'Глоссарий').map((f) => f.file_id);
  return sameSet(texts, data.text_files.map((f) => `${f.file_id}:${f.file_name}`)) && sameSet(glossaries, data.glossary_file_ids);
}

/** Файлы «Текст» и «Глоссарий» колонки — из списка файлов; остальное (разбор, записи) — как было */
function withFiles(data: GlossaryData, files: readonly ProjectFileRef[]): GlossaryData {
  const pages = new Map(data.text_files.map((f) => [f.file_id, f.pages]));
  const text_files = files
    .filter((f) => f.file_type === 'Текст')
    .map((f) => ({ file_id: f.file_id, file_name: f.file_name, pages: f.file_pages ?? pages.get(f.file_id) ?? null }));
  // Файл «Глоссарий», которого нет в строках «Разобрать», — уже разобран: строку ему не возвращаем
  const waiting = new Set(data.glossary_files.map((g) => g.file_id));
  const applied = new Set(data.glossary_file_ids.filter((id) => !waiting.has(id)));
  const glossary = files.filter((f) => f.file_type === 'Глоссарий');
  return {
    ...data,
    text_files,
    // Без разбора статус — только от файлов, как у сервера (routes/projects/glossary.ts textState); с разбором — его
    text_status: data.text_run ? data.text_status : text_files.length > 0 ? 'idle' : 'none',
    glossary_files: glossary.filter((f) => !applied.has(f.file_id)).map((f) => ({ file_id: f.file_id, file_name: f.file_name })),
    glossary_file_ids: glossary.map((f) => f.file_id),
  };
}

/**
 * Тип файла поменяли в этой вкладке (список карточки обновляется сразу и откатывается при ошибке) — колонка показывает
 * строки «Разобрать» сразу, не дожидаясь сервера; откат списка откатывает и колонку. Сверка — по эху project:updated.
 */
export function showProjectFiles(qc: QueryClient, projectId: string, files: readonly ProjectFileRef[]): void {
  const key = glossaryQueryKey(projectId);
  const data = qc.getQueryData<GlossaryData>(key);
  if (!data || sameFiles(data, files)) return;
  const next = withFiles(data, files);
  // Подтверждены сервером — файлы последнего ответа сервера; показанные сверх них — ждут (откат списка их убирает)
  const map = aheadOf(qc);
  const before = map.get(projectId);
  const confirmed = new Set(fileIdsOf(data).filter((id) => !before?.has(id)));
  map.set(projectId, new Set(fileIdsOf(next).filter((id) => !confirmed.has(id))));
  qc.setQueryData<GlossaryData>(key, next);
}

/** Сохранение файлов закончилось (успех или откат): показанное раньше сервера сверяется — и без события realtime */
export function reconcileProjectFiles(qc: QueryClient, projectId: string): void {
  const key = glossaryQueryKey(projectId);
  if (qc.getQueryData(key) !== undefined && hasAhead(qc, projectId)) void qc.invalidateQueries({ queryKey: key });
}

/**
 * «Разобрать» по файлу, который колонка показала раньше, чем сервер сохранил его тип: ждём сверки, иначе сервер
 * отказал бы («не тот файл»). Файл, не ждущий подтверждения, — сразу. false — сервер не подтвердил за 15 с.
 */
export function serverHasFiles(qc: QueryClient, projectId: string, fileIds: readonly string[]): Promise<boolean> {
  const key = glossaryQueryKey(projectId);
  const waiting = () => {
    const ahead = AHEAD.get(qc)?.get(projectId);
    return !!ahead && fileIds.some((id) => ahead.has(id));
  };
  if (!waiting()) return Promise.resolve(true);
  const hash = qc.getQueryCache().find({ queryKey: key, exact: true })?.queryHash;
  return new Promise((resolve) => {
    const finish = (ok: boolean) => {
      unsubscribe();
      clearTimeout(timer);
      resolve(ok);
    };
    const unsubscribe = qc.getQueryCache().subscribe((event) => {
      if (event.query.queryHash === hash && !waiting()) finish(true);
    });
    const timer = setTimeout(() => finish(false), CONFIRM_WAIT_MS);
  });
}

/**
 * Файлы проекта поменялись (project:updated: тип, имя, загрузка, удаление): колонка показывает их сразу и сверяется
 * с сервером (страницы, «разбор устарел»); эхо своей правки, уже показанной колонкой, — только сверка. Правка
 * проекта не о файлах колонку не трогает.
 */
export function applyProjectFilesChanged(qc: QueryClient, projectId: string, files: readonly ProjectFileRef[]): void {
  const key = glossaryQueryKey(projectId);
  const data = qc.getQueryData<GlossaryData>(key);
  if (!data) return;
  if (sameFiles(data, files)) {
    if (hasAhead(qc, projectId)) void qc.invalidateQueries({ queryKey: key });
    return;
  }
  qc.setQueryData<GlossaryData>(key, withFiles(data, files));
  void qc.invalidateQueries({ queryKey: key });
}

/** Ход разбора — в открытый глоссарий; закрытый не создаётся, файл не из разбора не трогается. */
export function applyBookTextProgress(qc: QueryClient, payload: BookTextProgress): void {
  const key = glossaryQueryKey(payload.projectId);
  const data = qc.getQueryData<GlossaryData>(key);
  const run = data?.text_run;
  if (!data || !run || !run.file_ids.includes(payload.fileId)) return;
  const item = { file_id: payload.fileId, percent: payload.percent, stage: payload.stage };
  const prev = run.progress.find((p) => p.file_id === payload.fileId);
  // Процент файла только растёт: запоздавшее событие полосу назад не тянет
  if (prev && prev.percent >= payload.percent) return;
  const progress = prev ? run.progress.map((p) => (p.file_id === payload.fileId ? item : p)) : [...run.progress, item];
  qc.setQueryData<GlossaryData>(key, { ...data, text_run: { ...run, progress } });
}

/**
 * Перечитанный глоссарий того же идущего разбора: процент файла не меньше уже показанного. Ответ, собранный до последнего
 * события, иначе откатывал бы полосу; другой разбор или законченный — как пришёл.
 */
export function keepRunProgress(prev: GlossaryData | undefined, next: GlossaryData): GlossaryData {
  const was = prev?.text_run;
  const run = next.text_run;
  if (!was || !run || run.status !== 'running' || was.status !== 'running' || was.started_at !== run.started_at) return next;
  const progress = run.progress.map((p) => {
    const old = was.progress.find((o) => o.file_id === p.file_id);
    return old && old.percent > p.percent ? old : p;
  });
  return { ...next, text_run: { ...run, progress } };
}
